import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, inject, model, OnDestroy } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { catchError, distinctUntilChanged, firstValueFrom, map, of, takeUntil } from "rxjs";
import { SharedModule } from "../../shared.module";
import { ActionButtonComponent } from "../ui-components/buttons/action-button/action-button.component";
import { FormInputComponent } from "../ui-components/forms/form-input/form-input.component";
import { DropdownComponent } from "../ui-components/dropdown/dropdown.component";
import { DropdownOption } from "../ui-components/dropdown/dropdown.model";
import { SaleTripService } from "src/app/pages/sale/sale-trip/sale-trip.service";
import { SobitService } from "src/app/services/sobit.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { FormContainerComponent } from "../ui-components/forms/form-container/form-container.component";
import { IBenefitsItem, IGetPaymentTypesResultData } from "@dert-services";
import { isNullOrUndefined } from "src/app/functions/common-functions";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentDialogBase } from "../payment-dialog-base/payment-dialog-base";
import { PaymentStatusEnum } from "src/app/enums/payment-status.enum";
import { OfflinePaymentDialogContext, PaymentDialogResult } from "src/app/models/payment-dialog.model";
import { IPaymentAttribute } from "src/app/services/model/pos.api.model";
import { AuthorizationPolicy, isAuthorizedForPolicy } from "src/app/guards/authorization-policy";

type BenefitPolicyLimitSource = "totalPricePercentage" | "remainingToPayFallback" | "explicitDialogLimit" | "notApplied" | "unavailable";

interface BenefitPolicyLimitCalculation {
  limit: number;
  basis: number | null;
  source: BenefitPolicyLimitSource;
  failClosed: boolean;
}

@Component({
  selector: "app-benefit-payment-dialog",
  templateUrl: "./benefit-payment-dialog.component.html",
  styleUrls: ["./benefit-payment-dialog.component.scss"],
  imports: [
    TranslateModule,
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    ActionButtonComponent,
    FormInputComponent,
    DropdownComponent,
    FontAwesomeModule,
    FormContainerComponent
],
  standalone: true,
})
export class BenefitPaymentDialogComponent extends PaymentDialogBase implements OnDestroy {

  private static readonly OVERPAYMENT_SUPPORTED_TENDER_TYPES = [
    TenderType.Cash,
    TenderType.Card,
    TenderType.Virtual,
    TenderType.ElectronicVoucher,
    TenderType.Cheque,
  ];

  private readonly _sobitService = inject(SobitService);
  private readonly _saleTripService = inject(SaleTripService);
  private readonly _httpClient = inject(HttpClient);
  
  readonly checked = model(false);
  
  public selectedPaymentsIds: string[] = [];
  public paymentType: IGetPaymentTypesResultData | null = null;
  public benefitsOptions: DropdownOption[] = [];
  public allBenefitsOptions: DropdownOption[] = [];
  public tenderTypes = TenderType;
  public selectedBenefitType: TenderType | null = null;
  public selectedBenefitTypeNominalsOption: DropdownOption[] | null = null;
  public maxBenefitPaymentValue: number = 0;
  public leftToPay: number = 0;
  public selectedPreFilter: TenderType | null = null;
  public preFilterOptions: DropdownOption[] = [];
  public benefitImageUrl: string | null = null;

  private maxBenefitPaymentOverrideLimit: number = 0;
  private benefitImageRequestId = 0;

  public confirmForm = new FormGroup({
    amountToPay: new FormControl<number>(null),
    selectAllCheckbox: new FormControl<boolean>(false),
    selectedBenefit: new FormControl<string>(null),
    preFilter: new FormControl<TenderType | null>(null),
    valueToPay: new FormControl<number>(null, [
      Validators.required,
      Validators.min(0),
      this.validateMaxValue(),
      this.validateMaxAllowedAmount(),
    ]),
    voucherCode: new FormControl<string>(null),
    voucherRowsGroup: new FormGroup({
      rows: new FormArray([], Validators.nullValidator)
    }),
    confirmationCode: new FormControl<string>(null, {
        validators: [Validators.minLength(6)],
        asyncValidators: [],
        updateOn: 'change'
      }),
    paymentType: new FormControl<TenderType>(null),
    paymentName: new FormControl<string>(null),
    allowOverpayment: new FormControl<boolean>(false),
    overpaymentReason: new FormControl<string | null>(null),
  });

  get tenderType(): TenderType {
    return this.selectedBenefitType || TenderType.ElectronicVoucher;
  }

  // ElectronicVoucher represents a benefit card processed by the terminal. The
  // manual offline shortcut follows the same authorization rules as card payments.
  get canOpenOfflinePayment(): boolean {
    return !this.isReturn
      && this.selectedBenefitType === TenderType.ElectronicVoucher
      && isAuthorizedForPolicy(this.loginService.user?.Roles, AuthorizationPolicy.Cancellation);
  }
  
  protected getPaymentTranslationKey(): string {
    return 'webPos.Benefits';
  }

  public override canUseOverpaymentOverride(): boolean {
    return !this.isReturn
      && BenefitPaymentDialogComponent.OVERPAYMENT_SUPPORTED_TENDER_TYPES.includes(this.selectedBenefitType);
  }

  public get displayedMaxBenefitPaymentValue(): number {
    return this.getActiveMaxBenefitPaymentLimit();
  }
  
  protected isFormValid(): boolean {
    return !this.isBenefitFormInvalid();
  }
  
  async ngOnInit() {

    if (this.data.paymentContext) await this._saleTripService.getBenefitTypes(this.data.paymentContext);
    const options = await this._saleTripService.getFormattedBenefitData(this.data.paymentContext);
    const allowedNames = new Set(this.filterAvailablePayments(this._saleTripService.loadedBenefits).map(p => p.Name));
    this.allBenefitsOptions = options.filter(option => allowedNames.has(option.value));

    this.preFilterOptions = [
      { label: 'webPos.Cheque', value: TenderType.Cheque },
      { label: 'webPos.Web', value: TenderType.Virtual },
      { label: 'webPos.Terminal', value: TenderType.ElectronicVoucher },
    ];

    this.benefitsOptions = this.allBenefitsOptions;
    this.leftToPay = this.data.remainingToPay;

    await this.setMaximumBenefitPayment();
    this.setupFormSubscriptions();

    // RxJS: react to pre-filter changes
    this.confirmForm.controls.preFilter.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged(),
    ).subscribe(value => this.handlePreFilterChange(value as TenderType | null));

    // RxJS: react to benefit selection
    this.confirmForm.controls.selectedBenefit.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged(),
    ).subscribe(value => {
      if (value) {
        this.handleBenefitSelection(value);
      }
    });

    if (this.allBenefitsOptions.length === 1) {
      const onlyOption = this.allBenefitsOptions[0];
      const onlyBenefit = this._saleTripService.loadedBenefits.find(benefit => benefit.Name === onlyOption.value);
      if (onlyBenefit) {
        this.confirmForm.controls.preFilter.setValue(onlyBenefit.TenderType);
        this.confirmForm.controls.selectedBenefit.setValue(onlyOption.value);
      }
    }

    this.confirmForm.controls.allowOverpayment.valueChanges.pipe(
      takeUntil(this.destroyObservable$),
      distinctUntilChanged(),
    ).subscribe(() => {
      this.confirmForm.controls.valueToPay.updateValueAndValidity({ emitEvent: false });
      this.logActiveMaximumBenefitPaymentLimit();
    });

    // RxJS: keep valueToPay in sync with voucher rows total
    this.confirmForm.controls.voucherRowsGroup.valueChanges.pipe(
      takeUntil(this.destroyObservable$)
    ).subscribe(() => {
      if (this.selectedBenefitType === this.tenderTypes.Cheque &&
          this.selectedBenefitTypeNominalsOption?.length > 0) {
        const total = this.getTotalPaperValue();
        this.confirmForm.controls.valueToPay.setValue(total, { emitEvent: false });
      }
    });
  }

  public async onConfirm(openOfflinePayment = false) {
    // Recheck the privileged action in code instead of relying only on button visibility.
    if (openOfflinePayment && !this.canOpenOfflinePayment) {
      return;
    }

    if (this.confirmForm.invalid || this.isBenefitFormInvalid()) {
      this.confirmForm.markAllAsTouched();
      this.confirmForm.updateValueAndValidity();
      return;
    }

    const formValue = this.confirmForm.value;
    const benefitPaymentType = this.getBenefitPaymentType(formValue.paymentName);
    if (!benefitPaymentType || !this.filterAvailablePayments([benefitPaymentType]).length) return;
    const dialogResult = this.dialogResult as PaymentDialogResult;
    const resolvedPaymentName = benefitPaymentType?.Name ?? formValue.paymentName ?? "";
    const resolvedTenderType = benefitPaymentType?.TenderType ?? formValue.paymentType;

    dialogResult.PaymentAttributes ??= [];

    if (formValue.paymentType === TenderType.Cheque) {
      const benefitPaymentItems: IBenefitsItem[] = formValue.voucherRowsGroup.rows.map((row: any) => ({
        Count: Number(row.numberOfItems) || 0,
        Value: Number(row.value) || 0,
      }));
      if (benefitPaymentItems.length > 0) {
        dialogResult.PaymentAttributes.push({ Key: 'Items', Value: benefitPaymentItems });
      }
    }

    if (formValue.paymentType === TenderType.Virtual) {
      dialogResult.PaymentAttributes.push({ Key: 'ConfirmationCode', Value: formValue.confirmationCode });
    }

    if (formValue.paymentType === TenderType.Voucher || formValue.paymentType === TenderType.Cheque) {
      dialogResult.PaymentAttributes.push({ Key: 'VoucherCode', Value: formValue.voucherCode });
    }

    dialogResult.Amount = Number(formValue.valueToPay);
    dialogResult.PaymentType = resolvedTenderType;
    dialogResult.PaymentName = resolvedPaymentName;
    dialogResult.Currency = this.loginService.user.CurrencyCode;

    const resolvedPaymentDisplayName = benefitPaymentType?.Description ?? resolvedPaymentName;
    dialogResult.PaymentDescription = resolvedPaymentDisplayName;

    if (formValue.paymentType === TenderType.ElectronicVoucher) {
      dialogResult.offlinePaymentDialogContext = this.getOfflinePaymentDialogContext(benefitPaymentType);

      // The parent replaces this dialog with the offline card form. Keeping dialog
      // navigation in the parent prevents two full-screen Material dialogs stacking.
      if (openOfflinePayment) {
        dialogResult.openOfflineCardPaymentDialog = true;
      }
    }

    dialogResult.PaymentAttributes = this.appendOverpaymentAttributes(dialogResult.PaymentAttributes);

    this.dialogRef.close(dialogResult);
  }

  public async onOfflinePayment() {
    return this.onConfirm(true);
  }
  
  public async onSubmit() {
    return this.onConfirm();
  }

  public get rows(): FormArray {
    return this.confirmForm.controls.voucherRowsGroup.controls.rows;
  }

  private getBenefitPaymentType(paymentName?: string | null): IGetPaymentTypesResultData | undefined {
    if (!paymentName || (this.data.paymentContext && !this.data.allowedPaymentNames?.includes(paymentName))) {
      return undefined;
    }

    if (this.paymentType?.Name === paymentName) {
      return this.paymentType;
    }

    return this._saleTripService.loadedBenefits.find(benefit => benefit.Name === paymentName);
  }

  private getOfflinePaymentDialogContext(
    benefitPaymentType: IGetPaymentTypesResultData | undefined,
  ): OfflinePaymentDialogContext {
    const resolvedTenderType = benefitPaymentType?.TenderType ?? this.tenderType;
    const resolvedPaymentName = benefitPaymentType?.Name ?? this.confirmForm.controls.paymentName.value ?? "";
    const resolvedPaymentDisplayName = benefitPaymentType?.Description ?? resolvedPaymentName;

    return {
      paymentType: resolvedTenderType,
      paymentName: resolvedPaymentName,
      paymentDisplayName: resolvedPaymentDisplayName,
      currency: this.loginService.user.CurrencyCode,
      paymentAttributes: [
        {
          Key: "TenderType",
          Value: String(resolvedTenderType),
        },
        {
          Key: "TenderSubType",
          Value: benefitPaymentType?.TenderSubType ?? "",
        },
      ] satisfies IPaymentAttribute[],
    };
  }

  private getBenefitNominalsOptions(benefitPaymentType: IGetPaymentTypesResultData | null): DropdownOption[] {
    const nominals = benefitPaymentType?.Nominals
      ? [...benefitPaymentType.Nominals].sort((a, b) => a.sequence - b.sequence)
      : [];

    return nominals.map((nominal) => ({
      label: nominal.denomination,
      value: nominal.value,
    }));
  }

  public async setMaximumBenefitPayment() {
    const configuredValueLimit = Number(this.stateService.configuration?.maxBenefitPaymentValue ?? 20000);
    const configuredPercentage = Number(this.stateService.configuration?.maxBenefitPaymentPercentage ?? 50);

    const benefitPaymentTypes = (await this._sharedService.getBenefityTypes())
      .filter(pt => pt.IsBenefit);
    const benefitPaymentNames = benefitPaymentTypes.map(pt => pt.Name);

    const paymentBenefitClassification = this.data.payments.map(payment => {
      const isBenefitPaymentType = benefitPaymentNames.includes(payment.PaymentName);
      const hasCountedStatus = payment.Status === PaymentStatusEnum.Done
        || payment.Status === PaymentStatusEnum.Pending;

      return {
        paymentName: payment.PaymentName,
        amount: payment.Amount,
        status: payment.Status,
        isBenefitPaymentType,
        hasCountedStatus,
        isCountedAsPaidBenefit: isBenefitPaymentType && hasCountedStatus,
        reason: !isBenefitPaymentType
          ? "PaymentName is not configured as a benefit payment type"
          : hasCountedStatus
            ? "Benefit payment with Done or Pending status"
            : "Benefit payment is ignored because its status is neither Done nor Pending",
      };
    });

    console.info("[BenefitPaymentDialog] Payment benefit classification", {
      configuredBenefitPaymentTypes: benefitPaymentTypes.map(paymentType => ({
        name: paymentType.Name,
        description: paymentType.Description,
        tenderType: paymentType.TenderType,
      })),
      payments: paymentBenefitClassification,
    });

    const paidBenefitPayments = this.data.payments
      .filter(item => benefitPaymentNames.includes(item.PaymentName)
        && (item.Status === PaymentStatusEnum.Done || item.Status === PaymentStatusEnum.Pending));

    const payedByBenefits = paidBenefitPayments
      .reduce((sum, item) => sum + item.Amount, 0);

    const absoluteLimit = Math.max(configuredValueLimit - payedByBenefits, 0);
    const policyCalculation = this.getBenefitPolicyLimit(configuredPercentage, payedByBenefits);
    const policyLimit = policyCalculation.limit;

    let standardLimit = absoluteLimit;
    let overrideLimit = absoluteLimit;

    standardLimit = Math.min(standardLimit, policyLimit);

    if (this.data?.maxPaymentValue !== null && this.data?.maxPaymentValue !== undefined) {
      standardLimit = Math.min(standardLimit, this.data.maxPaymentValue);
      overrideLimit = Math.min(overrideLimit, this.data.maxPaymentValue);
    }

    if (this.leftToPay !== null && this.leftToPay !== undefined) {
      standardLimit = Math.min(standardLimit, this.leftToPay);
    }

    if (policyCalculation.failClosed) {
      overrideLimit = 0;
    }

    this.maxBenefitPaymentValue = Math.max(standardLimit, 0);
    this.maxBenefitPaymentOverrideLimit = Math.max(overrideLimit, 0);

    console.info("[BenefitPaymentDialog] Maximum benefit payment calculation", {
      inputs: {
        configuredValueLimit,
        configuredPercentage,
        totalPrice: this.data.totalPrice,
        remainingToPay: this.leftToPay,
        explicitDialogLimit: this.data.maxPaymentValue ?? null,
      },
      countedBenefitPayments: paidBenefitPayments.map(payment => ({
        paymentName: payment.PaymentName,
        amount: payment.Amount,
        status: payment.Status,
      })),
      limits: {
        alreadyPaidByBenefits: payedByBenefits,
        absoluteLimit,
        policyLimit,
        policyLimitBasis: policyCalculation.basis,
        policyLimitSource: policyCalculation.source,
        failClosed: policyCalculation.failClosed,
        remainingToPayLimit: this.leftToPay ?? null,
        explicitDialogLimit: this.data.maxPaymentValue ?? null,
      },
      rules: {
        standard: "min(absoluteLimit, policyLimit, remainingToPayLimit, explicitDialogLimit)",
        overpaymentOverride: "min(absoluteLimit, explicitDialogLimit)",
      },
      result: {
        standardLimit: this.maxBenefitPaymentValue,
        overpaymentOverrideLimit: this.maxBenefitPaymentOverrideLimit,
        displayedLimit: this.maxBenefitPaymentValue,
      },
    });
  }

  /** Handles pre-filter dropdown changes  driven by preFilter.valueChanges */
  private handlePreFilterChange(tenderTypeValue: TenderType | null) {
    this.selectedPreFilter = tenderTypeValue;
    this.selectedBenefitType = null;
    this.selectedBenefitTypeNominalsOption = null;
    this.paymentType = null;
    this.resetOverpaymentOverride();

    // Remove rows first (before resetting validators/values)
    while (this.confirmForm.controls.voucherRowsGroup.controls.rows.length > 0) {
      this.confirmForm.controls.voucherRowsGroup.controls.rows.removeAt(0);
    }

    // Reset validators to defaults BEFORE clearing values to prevent cascading validation events
    this.confirmForm.controls.confirmationCode.setValidators([Validators.minLength(6)]);
    this.confirmForm.controls.voucherCode.setValidators([]);

    // Silence all value changes when resetting
    const silent = { emitEvent: false };
    this.confirmForm.controls.selectedBenefit.setValue(null, silent);
    this.confirmForm.controls.paymentName.setValue(null, silent);
    this.confirmForm.controls.paymentType.setValue(null, silent);
    this.confirmForm.controls.valueToPay.setValue(null, silent);
    this.confirmForm.controls.voucherCode.setValue(null, silent);
    this.confirmForm.controls.confirmationCode.setValue(null, silent);
    this.confirmForm.controls.confirmationCode.updateValueAndValidity({ emitEvent: false });
    this.confirmForm.controls.voucherCode.updateValueAndValidity({ emitEvent: false });
    this.confirmForm.controls.valueToPay.updateValueAndValidity({ emitEvent: false });

    // Filter benefits list
    this.benefitsOptions = tenderTypeValue === null
      ? this.allBenefitsOptions
      : this.allBenefitsOptions.filter(option => {
          const benefitItem = this._saleTripService.loadedBenefits.find(b => b.Name === option.value);
          return benefitItem && benefitItem.TenderType === tenderTypeValue;
        });
  }

  /** Handles benefit dropdown changes  driven by selectedBenefit.valueChanges */
  private handleBenefitSelection(paymentName: string) {
    const silent = { emitEvent: false };
    this.confirmForm.controls.valueToPay.setValue(null, silent);
    this.confirmForm.controls.voucherCode.setValue(null, silent);
    this.confirmForm.controls.confirmationCode.setValue(null, silent);

    while (this.confirmForm.controls.voucherRowsGroup.controls.rows.length > 0) {
      this.confirmForm.controls.voucherRowsGroup.controls.rows.removeAt(0);
    }

    this.resetOverpaymentOverride();

    const selectedBenefitPaymentType = this.getBenefitPaymentType(paymentName) ?? null;

    this.paymentType = selectedBenefitPaymentType;
    this.selectedBenefitType = selectedBenefitPaymentType?.TenderType ?? null;
    this.selectedBenefitTypeNominalsOption = this.getBenefitNominalsOptions(selectedBenefitPaymentType);
    void this.updateBenefitImage(selectedBenefitPaymentType);

    this.confirmForm.controls.paymentName.setValue(selectedBenefitPaymentType?.Name ?? paymentName, silent);
    this.confirmForm.controls.paymentType.setValue(selectedBenefitPaymentType?.TenderType ?? null, silent);

    this.updateConfirmationCodeValidation();
    this.updateVoucherCodeValidation();
    this.confirmForm.controls.valueToPay.updateValueAndValidity({ emitEvent: false });

    if (this.selectedBenefitType === this.tenderTypes.Cheque &&
        this.selectedBenefitTypeNominalsOption?.length > 0) {
      this.addRow();
    }
  }

  private updateConfirmationCodeValidation() {
    const ctrl = this.confirmForm.controls.confirmationCode;

    const validationRegex = this.paymentType?.Attributes?.find(a => a.Key === 'ValidationRegex')?.Value;
    const validators = [];

    if (this.selectedBenefitType === this.tenderTypes.Virtual) {
      validators.push(Validators.required);
    }

    if (validationRegex) {
      validators.push(Validators.pattern(new RegExp(validationRegex)));
    } else {
      validators.push(Validators.minLength(6));
    }

    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private updateVoucherCodeValidation() {
    const ctrl = this.confirmForm.controls.voucherCode;
    ctrl.setValidators(
      this.selectedBenefitType === this.tenderTypes.Voucher ? [Validators.required] : []
    );
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  override ngOnDestroy() {
    this.benefitImageRequestId++;
    this.destroyObservable$.next(null);
    this.destroyObservable$.complete();
    super.ngOnDestroy?.();
  }

  public onBenefitImageError(imageUrl: string): void {
    if (this.benefitImageUrl !== imageUrl) {
      return;
    }

    this.benefitImageUrl = null;
    const expectedFileName = decodeURIComponent(imageUrl.substring(imageUrl.lastIndexOf("/") + 1));
    console.warn(`[BenefitPaymentDialog] Benefit payment image not found: ${expectedFileName}`, {
      expectedFile: imageUrl,
      expectedFileName,
    });
  }

  private async updateBenefitImage(paymentType: IGetPaymentTypesResultData | null): Promise<void> {
    const requestId = ++this.benefitImageRequestId;
    this.benefitImageUrl = null;

    if (!paymentType?.Name) {
      return;
    }

    const imageUrl = `/assets/payment_types/${encodeURIComponent(paymentType.Name)}.png`;
    const imageExists = await firstValueFrom(
      this._httpClient.head(imageUrl, { observe: "response" }).pipe(
        map(response => response.ok),
        catchError(() => of(false)),
      ),
    );

    if (requestId !== this.benefitImageRequestId) {
      return;
    }

    if (imageExists) {
      this.benefitImageUrl = imageUrl;
      return;
    }

    const expectedFileName = decodeURIComponent(imageUrl.substring(imageUrl.lastIndexOf("/") + 1));
    console.warn(`[BenefitPaymentDialog] Benefit payment image not found: ${expectedFileName}`, {
      expectedFile: imageUrl,
      expectedFileName,
    });
  }

  openExternalPage() {
    if (this.paymentType) {
      const beneLinkAttr = this.paymentType.Attributes?.find(a => a.Key === 'BenefityWeb');
      window.open(beneLinkAttr.Value, 'benefityWeb');
    }
  }

  isBenefitFormInvalid(): boolean {
    if (this.selectedBenefitType == this.tenderTypes.Virtual) {
      return this.confirmForm.controls.confirmationCode.invalid || this.confirmForm.controls.valueToPay.invalid;
    }
    if (this.selectedBenefitType == this.tenderTypes.ElectronicVoucher) {
      return this.confirmForm.controls.valueToPay.invalid;
    }
    if (this.selectedBenefitType == this.tenderTypes.Voucher) {
      return this.confirmForm.controls.voucherCode.invalid || this.confirmForm.controls.valueToPay.invalid;
    }
    if (this.selectedBenefitType == this.tenderTypes.Cheque) {
      const hasNominals = this.selectedBenefitTypeNominalsOption && this.selectedBenefitTypeNominalsOption.length > 0;
      const activeMaxLimit = this.getActiveMaxBenefitPaymentLimit();
      if (hasNominals) {
        const totalValue = this.getTotalPaperValue();
        return !(this.confirmForm.controls.voucherRowsGroup.controls.rows.valid && totalValue <= activeMaxLimit);
      } else {
        const valueToPay = Number(this.confirmForm.get('valueToPay')?.value);
        return !(Number.isFinite(valueToPay) && valueToPay > 0 && valueToPay <= activeMaxLimit);
      }
    }
    return true;
  }

  getTotalPaperValue(): number {
    let total = 0;
    this.confirmForm.controls.voucherRowsGroup.controls.rows.value.forEach(row => {
      const value = Number(row.value);
      const numberOfItems = Number(row.numberOfItems);
      if (!isNaN(value) && !isNaN(numberOfItems) && value > 0 && numberOfItems > 0) {
        total += value * numberOfItems;
      }
    });
    return total;
  }

  addRow() {
    this.confirmForm.controls.voucherRowsGroup.controls.rows.push(new FormGroup({
      numberOfItems: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
      value: new FormControl<number | null>(null, [Validators.required]),
    }));

    const scrollContainer = document.getElementById('voucherRows');
    if (scrollContainer) {
      setTimeout(() => scrollContainer.scrollTo(0, scrollContainer.scrollHeight), 0);
    }
  }

  removeRow(index: number) {
    if (this.confirmForm.controls.voucherRowsGroup.controls.rows.length <= 1) {
      return;
    }
    this.confirmForm.controls.voucherRowsGroup.controls.rows.removeAt(index);
  }

  public validateMaxValue(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      const value = Number(control.value);
      const maxLimit = this.getActiveMaxBenefitPaymentLimit();

      if (isNaN(value)) {
        return { notANumber: true };
      }

      if (!Number.isFinite(maxLimit)) {
        return null;
      }

      return value > maxLimit ? { maxValueExceeded: true } : null;
    };
  }

  private validateMaxAllowedAmount(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      const value = Number(control.value);
      const maxLimit = this.getActiveMaxBenefitPaymentLimit();

      if (isNaN(value) || !Number.isFinite(maxLimit)) {
        return null;
      }

      return value > maxLimit ? { maxAllowedExceeded: true } : null;
    };
  }

  private getActiveMaxBenefitPaymentLimit(): number {
    return this.isOverpaymentOverrideEnabled()
      ? this.maxBenefitPaymentOverrideLimit
      : this.maxBenefitPaymentValue;
  }

  private logActiveMaximumBenefitPaymentLimit(): void {
    const overpaymentOverrideEnabled = this.isOverpaymentOverrideEnabled();

    console.info("[BenefitPaymentDialog] Active maximum benefit payment changed", {
      selectedBenefitType: this.selectedBenefitType,
      overpaymentOverrideRequested: this.confirmForm.controls.allowOverpayment.value,
      overpaymentOverrideEnabled,
      appliedRule: overpaymentOverrideEnabled ? "overpaymentOverride" : "standard",
      displayedLimit: this.getActiveMaxBenefitPaymentLimit(),
    });
  }

  private getBenefitPolicyLimit(
    configuredPercentage: number,
    payedByBenefits: number,
  ): BenefitPolicyLimitCalculation {
    if (this.isReturn && this.data.returnAmountFromPayments) {
      return {
        limit: this.amountToReturn,
        basis: this.amountToReturn,
        source: "explicitDialogLimit",
        failClosed: false,
      };
    }

    if (this.data.skipBenefitPriceLimits) {
      console.info("[BenefitPaymentDialog] Price-based benefit limits are not applied for fly-ticket/eTravel", {
        totalPrice: this.data.totalPrice,
        remainingToPay: this.data.remainingToPay,
        explicitDialogLimit: this.data.maxPaymentValue ?? null,
      });

      return {
        limit: Number.POSITIVE_INFINITY,
        basis: null,
        source: "notApplied",
        failClosed: false,
      };
    }

    if (!isNullOrUndefined(this.data.totalPrice) && Number.isFinite(this.data.totalPrice)) {
      return {
        limit: Math.max(this.data.totalPrice * (configuredPercentage / 100) - payedByBenefits, 0),
        basis: this.data.totalPrice,
        source: "totalPricePercentage",
        failClosed: false,
      };
    }

    if (!isNullOrUndefined(this.data.remainingToPay) && Number.isFinite(this.data.remainingToPay)) {
      console.warn("[BenefitPaymentDialog] totalPrice is unavailable; using remainingToPay as the safe maximum", {
        totalPrice: this.data.totalPrice,
        remainingToPay: this.data.remainingToPay,
      });

      return {
        limit: Math.max(this.data.remainingToPay, 0),
        basis: this.data.remainingToPay,
        source: "remainingToPayFallback",
        failClosed: false,
      };
    }

    if (!isNullOrUndefined(this.data.maxPaymentValue) && Number.isFinite(this.data.maxPaymentValue)) {
      console.warn("[BenefitPaymentDialog] Price basis is unavailable; using the explicit dialog limit", {
        totalPrice: this.data.totalPrice,
        remainingToPay: this.data.remainingToPay,
        explicitDialogLimit: this.data.maxPaymentValue,
      });

      return {
        limit: Math.max(this.data.maxPaymentValue, 0),
        basis: this.data.maxPaymentValue,
        source: "explicitDialogLimit",
        failClosed: false,
      };
    }

    console.error("[BenefitPaymentDialog] Cannot calculate a safe benefit percentage limit; payment is blocked", {
      totalPrice: this.data.totalPrice,
      remainingToPay: this.data.remainingToPay,
      explicitDialogLimit: this.data.maxPaymentValue ?? null,
    });

    return {
      limit: 0,
      basis: null,
      source: "unavailable",
      failClosed: true,
    };
  }
}
